import React, { createContext } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import SubscriptionServices from './Services/Consumers/SubscriptionServices'
import { NamedRoutes } from './routes'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { read, utils } from 'xlsx'

const ExternalEntityGroupContext = createContext({})

const ExternalEntityGroupProvider = ({ children, externalEntityGroupId, isEdit, handleNotificationCall }) => {
  const { keycloak } = useKeycloak()
  const intl = useIntl()
  const history = useHistory()
  const [externalServiceHandlers, setExternalServiceHandlers] = React.useState([])
  const [externalEntityGroupData, setExternalEntityGroupData] = React.useState({})
  const [isLoading, setIsLoading] = React.useState(false)
  const [externalEntityGroupDataFailure, setExternalEntityGroupDataFailure] = React.useState(false)
  const [bulkImportHasError, setBulkImportHasError] = React.useState(false)

  React.useEffect(() => {
    getExternalServiceHandlers()
    if (isEdit && !!externalEntityGroupId && !!parseInt(externalEntityGroupId)) {
      getExternalEntityGroupData()
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const getExternalServiceHandlers = () => {
    if (!!keycloak?.authenticated) {
      SubscriptionServices.getExternalServiceHandlers({
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }).then((result) => {
        if (!!result && !!result.data && result.data.status.code === 200) {
          setExternalServiceHandlers(result.data.data || [])
        }
      })
    }
  }

  const getExternalEntityGroupData = () => {
    if (!!keycloak?.authenticated) {
      setIsLoading(true)
      setExternalEntityGroupDataFailure(false)
      SubscriptionServices.getExternalEntityGroupData(externalEntityGroupId, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }).then((result) => {
        if ((!!result && result.status !== 200) || !result) {
          setExternalEntityGroupDataFailure(true)
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            setExternalEntityGroupData(result.data.data)
          } else {
            setExternalEntityGroupDataFailure(true)
          }
        }
      }).then(() => {
        setIsLoading(false)
      })
    }
  }

  const handleSubmit = (values, FormikActions) => {
    if (!!keycloak?.authenticated) {
      FormikActions.setSubmitting(true)
      SubscriptionServices.submitExternalEntityGroup(
        externalEntityGroupId,
        isEdit,
        values,
        { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
        null,
        null,
        FormikActions
      ).then((result) => {
        if (result && result.data && result.status === 200) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({
              id: isEdit ? 'ExternalEntityGroups.SuccessUpdate' : 'ExternalEntityGroups.SuccessCreate'
            }),
            color: 'success'
          }, 'success')
          history.push(NamedRoutes.subscription.externalEntityGroups.list)
        } else {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response && result.response.data) {
            if (result.response.data && result.response.data.status && result.response.data.status.errors) {
              message = result.response.data.status.errors
            }
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        }
      })
    }
  }

  const handleBulkImportSubmit = async (values, FormikActions, closeDialog, reloadPage) => {
    if (!!keycloak?.authenticated) {
      try {
        const file = values.externalEntityGroupsFile

        const fileBuffer = await file.arrayBuffer()
        const workbook = read(fileBuffer)
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const data = utils.sheet_to_json(worksheet)

        if (!data.length) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({ id: 'ExternalEntityGroupBulkImport.file.empty' }),
            color: 'error'
          }, 'error')
          setTimeout(() => {
            closeDialog()
          }, 500)
          return
        }

        const payloadRows = data.map(row => ({
          name: row['name'],
          entity_type: row['entity_type'],
          entity_references: row['entity_references']?.split(';').map(ref => ref.trim())
        }))

        const bulkImportPayload = { data: payloadRows }

        const result = await SubscriptionServices.bulkImportExternalEntityGroups(
          bulkImportPayload,
          { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
          null,
          null,
          FormikActions
        )

        if (result && result.data && result.status === 200) {
          const count = result.data.data?.created || result.data.data?.updated || 0
          setBulkImportHasError(false)
          if (!!count) {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'ExternalEntityGroupBulkImport.success.message' }, { count }),
              color: 'success'
            }, 'success')
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'ExternalEntityGroupBulkImport.noData' }),
              color: 'error'
            }, 'error')
          }
          FormikActions.setSubmitting(false)
          FormikActions.resetForm()
          // Close dialog and reload page
          closeDialog()
          reloadPage(true)
        } else {
          throw new Error(result?.response?.data?.status?.errors || result?.response?.data?.status?.message || intl.formatMessage({ id: 'bulkImport.failed' }))
        }
      } catch (error) {
        setBulkImportHasError(true)
        handleNotificationCall({
          type: 'defence',
          message: error.message || intl.formatMessage({ id: 'bulkImport.error' }),
          color: 'error'
        }, 'error')
        FormikActions.setSubmitting(false)
        FormikActions.resetForm()
      }
    }
  }

  const clearBulkImportError = () => {
    setBulkImportHasError(false)
  }

  return (
    <ExternalEntityGroupContext.Provider value={{
      externalServiceHandlers,
      externalEntityGroupData,
      isLoading,
      externalEntityGroupDataFailure,
      bulkImportHasError,
      isEdit,

      getExternalEntityGroupData,
      getExternalServiceHandlers,
      handleSubmit,
      handleBulkImportSubmit,
      clearBulkImportError
    }}>
      {children}
    </ExternalEntityGroupContext.Provider>
  )
}

const ExternalEntityGroupConsumer = ExternalEntityGroupContext.Consumer

export { ExternalEntityGroupProvider, ExternalEntityGroupConsumer }